import React from 'react';
import { Plus, ShoppingBag, RotateCcw, Copy, Loader2, X, Trash2 } from 'lucide-react';
import { Button } from '../ui/button';

const AddAnotherShadePanel = ({
  shades = [],
  lastRoom,
  cartCount,
  onSameRoom,
  onNewRoom,
  onStartFresh,
  onClose,
  onViewCart,
  onDuplicate,
  duplicatingIndex,
  onRemoveShade,
  onClearShades,
}) => (
  <div className="bg-green-50 border border-green-200 rounded-xl p-5 space-y-4" data-testid="add-another-panel">
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-2">
        <div className="w-7 h-7 bg-green-500 rounded-full flex items-center justify-center">
          <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" /></svg>
        </div>
        <p className="font-medium text-neutral-900">Shade added to your cart</p>
      </div>
      <button onClick={onClose} className="text-neutral-400 hover:text-neutral-600 transition-colors p-1" data-testid="close-added-panel">
        <X className="w-4 h-4" />
      </button>
    </div>

    <div className="flex flex-wrap gap-2">
      {lastRoom && (
        <Button variant="outline" size="sm" onClick={onSameRoom} className="bg-white" data-testid="same-room-btn">
          <Plus className="w-4 h-4 mr-1.5" /> Another window in {lastRoom}
        </Button>
      )}
      <Button variant="outline" size="sm" onClick={onNewRoom} className="bg-white" data-testid="new-room-btn">
        <Plus className="w-4 h-4 mr-1.5" /> New room
      </Button>
      <Button variant="outline" size="sm" onClick={onStartFresh} className="bg-white" data-testid="start-fresh-btn">
        <RotateCcw className="w-4 h-4 mr-1.5" /> Start fresh
      </Button>
      <Button variant="outline" size="sm" onClick={onViewCart} className="bg-white" data-testid="view-cart-btn">
        <ShoppingBag className="w-4 h-4 mr-1.5" /> View cart ({cartCount})
      </Button>
    </div>

    {/* Session shades list */}
    {shades.length > 0 && (
      <div>
        <div className="flex items-center justify-between mb-1.5">
          <p className="text-xs text-neutral-500 font-medium uppercase tracking-wide">Added this session</p>
          {shades.length > 1 && onClearShades && (
            <button
              onClick={onClearShades}
              className="text-[11px] text-neutral-400 hover:text-red-500 flex items-center gap-1 transition-colors"
              data-testid="clear-session-shades"
            >
              <Trash2 className="w-3 h-3" /> Clear history
            </button>
          )}
        </div>
        <ul className="space-y-1.5">
          {shades.map((s, i) => (
            <li key={i} className="flex items-center gap-2 bg-white rounded-lg border px-3 py-2 text-sm group" data-testid={`session-shade-${i}`}>
              {s.swatch ? (
                <img src={s.swatch} className="w-6 h-6 rounded-full border" alt="" />
              ) : s.hex ? (
                <span className="w-6 h-6 rounded-full border" style={{ backgroundColor: s.hex }} />
              ) : null}
              <span className="text-neutral-600 flex-1 truncate">
                <strong>{s.room}</strong>
                {s.window ? ` — ${s.window}` : ''}
                <span className="text-neutral-400 ml-1.5">{s.width}" × {s.height}"</span>
                {s.color && <span className="text-neutral-400 ml-1.5">· {s.color}</span>}
              </span>
              <span className="text-xs text-neutral-400 tabular-nums shrink-0">${(s.total || 0).toFixed(2)}</span>
              <button
                onClick={() => onDuplicate?.(i)}
                disabled={duplicatingIndex === i}
                className="p-1 text-neutral-300 hover:text-amber-600 transition-colors"
                title="Duplicate shade"
                data-testid={`duplicate-shade-${i}`}
              >
                {duplicatingIndex === i ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Copy className="w-3.5 h-3.5" />}
              </button>
              {onRemoveShade && (
                <button
                  onClick={() => onRemoveShade(i)}
                  className="p-1 text-neutral-200 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-all"
                  title="Remove from history"
                  data-testid={`remove-shade-${i}`}
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </li>
          ))}
        </ul>
      </div>
    )}
  </div>
);

export default AddAnotherShadePanel;
